import {
  AbsoluteCenter,
  Box,
  Editable,
  EmptyState,
  FormatNumber,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  LocaleProvider,
  Select,
  SkeletonText,
  Table,
  Text,
  VStack,
  createListCollection,
} from '@chakra-ui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  type GroupingState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LuFileSpreadsheet, LuPencilLine } from 'react-icons/lu';
import { MdOutlineExpandMore } from 'react-icons/md';

import { type IndicatorCollectionFrequency, IndicatorsPeriodsService } from '@/client/index.ts';
import Loader from '@/components/Loader.tsx';
import { CTag } from '@/components/common/CTag.tsx';
import { Pagination } from '@/components/common/Pagination.tsx';
import { ResultNotFound } from '@/components/common/ResultNotFound.tsx';
import IndicatorDrawer from '@/components/indicators//index/drawer/IndicatorDrawer.tsx';
import { category } from '@/components/indicators/index/IndicatorData.tsx';
import { Tooltip } from '@/components/ui/tooltip';
import useCustomToast from '@/hooks/use-custom-toast.ts';
import { Route } from '@/routes/_layout/$slug/indicators/collecte/$year/index.tsx';

function getIndicatorsPeriodsQueryOptions({
  slug,
  year,
  period,
  entity_id,
  page,
  pageSize,
  filter,
  sort,
  search,
  group_by,
}: {
  slug: string;
  year: number;
  period: string;
  entity_id: string;
  page: number;
  pageSize: number;
  filter: string;
  sort: string;
  search: string;
  group_by: string;
}) {
  return {
    queryKey: ['indicators_periods', { page, pageSize, filter, sort, search }],
    queryFn: async () => {
      if (!year || !period) {
        return {
          data: [],
          count: 0,
        };
      }
      return await IndicatorsPeriodsService.readIndicatorPeriods({
        year: year,
        period: period.match(/([a-zA-Z]+)(\d+)/)?.[1] as IndicatorCollectionFrequency,
        periodNumber: Number.parseInt(period.match(/([a-zA-Z]+)(\d+)/)?.[2] || '0'),
        entityId: entity_id !== '' ? entity_id : undefined,
        offset: group_by !== '' ? 1 : (page - 1) * pageSize,
        limit: group_by !== '' ? 9999 : pageSize,
        companySlug: slug,
        filters: filter,
        sort: sort,
        search: search,
      });
    },
  };
}

export function CollecteTable() {
  const navigate = useNavigate({ from: Route.fullPath });
  const { period, entity_id, page, pageSize, filter, sort, search, group_by } = Route.useSearch();
  const showToast = useCustomToast();
  const { t } = useTranslation();
  const { slug, year } = useParams({ strict: false });
  const [count, setCount] = useState<number>(0);
  const [grouping, setGrouping] = useState<GroupingState>([]);
  const [indicatorDetailsId, setIndicatorDetailsId] = useState<string | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const {
    data: indicatorsPeriodsData,
    isLoading,
    refetch,
  } = useQuery({
    ...getIndicatorsPeriodsQueryOptions({
      slug,
      year,
      period,
      entity_id,
      page,
      pageSize,
      filter,
      sort,
      search,
      group_by,
    }),
  });

  const mutation = useMutation({
    mutationFn: ({ id, value, objective }: { id: string; value: string; objective: string }) =>
      IndicatorsPeriodsService.updateIndicatorPeriod({
        id,
        companySlug: slug,
        requestBody: {
          value: Number.parseFloat(value),
          objective: Number.parseFloat(objective),
        },
      }),
    onSuccess: () => {
      refetch();
      showToast('Success', 'Indicator value updated successfully', 'success');
    },
    onError: () => {
      refetch();
      showToast('Error', 'An error occured while updating the indicator value', 'error');
    },
  });

  const handleChange = (id: string, value: string, objective: string) => {
    mutation.mutate({ id, value, objective });
  };

  const isEmpty = (v: any) => v === null || v === undefined || v === '';

  const areEqual = (a: any, b: any) => {
    if (isEmpty(a) && isEmpty(b)) return true;
    return a === b;
  };

  useEffect(() => {
    if (indicatorsPeriodsData && indicatorsPeriodsData.count !== undefined) {
      setCount(indicatorsPeriodsData.count);
    }
  }, [indicatorsPeriodsData]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (grouping.length === 0 && group_by !== '') setGrouping([group_by]);
    else if (grouping.length > 0 && grouping[0] !== group_by) {
      setGrouping([group_by]);
    }
    refetch();
  }, [year, period, entity_id, page, filter, pageSize, sort, search, group_by]);

  useEffect(() => {
    if (indicatorsPeriodsData && count < page * pageSize && page > 1) {
      navigate({
        search: (prev: Record<string, string>) => ({
          ...prev,
          page: Math.floor(count / pageSize) + 1,
        }),
      });
    }
  });

  const columns = [
    {
      accessorKey: 'indicator_name',
      baseKey: 'indicator_year_setting___indicator___name',
      header: t('collection.columnsHeader.indicatorName'),
      size: 200,
      options: {
        enableSorting: true,
      },
      cell: ({ row }: { row: any }) => (
        <HStack>
          <IconButton
            variant="outline"
            aria-label="info"
            size="2xs"
            colorPalette="gray"
            borderRadius={'full'}
            color={'gray.500'}
            _hover={{ color: 'white' }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setIndicatorDetailsId(row.original.indicator_id);
            }}
          >
            <LuFileSpreadsheet />
          </IconButton>
          {row.original.reference_year === true && (
            <Tooltip
              showArrow
              content={t('collection.manage.columnsHeader.referenceYear')}
              contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
              openDelay={100}
              closeDelay={100}
            >
              <span style={{ cursor: 'pointer' }}>✨</span>
            </Tooltip>
          )}
          <Box>
            <Text fontWeight={'600'}>{row.original.indicator_name}</Text>
            {!row.original.id && (
              <Text color="gray.500" textStyle="xs" fontStyle={'italic'}>
                Consolidé
              </Text>
            )}
          </Box>
        </HStack>
      ),
    },
    {
      accessorKey: 'pillar_name',
      baseKey: 'indicator_year_setting___indicator___commitment___pillar___name',
      header: t('collection.columnsHeader.pillarName'),
      options: {
        enableSorting: true,
        enableGrouping: true,
      },
      cell: ({ row }: { row: any }) => (
        <CTag label={row.original.pillar_name ? row.original.pillar_name : 'N/R'} labelToColor={true} />
      ),
    },
    {
      accessorKey: 'commitment_name',
      baseKey: 'indicator_year_setting___indicator___commitment___name',
      header: t('collection.columnsHeader.commitmentName'),
      options: {
        enableSorting: true,
        enableHiding: true,
        enableGrouping: true,
      },
      cell: ({ row }: { row: any }) => {
        return (
          row.original.commitment_name || (
            <Text color="gray.500" textStyle="sm">
              N/R
            </Text>
          )
        );
      },
    },
    {
      accessorKey: 'category',
      baseKey: 'indicator_year_setting___indicator___category',
      header: () => t('indicator.columnsHeader.categoryName'),
      size: 160,
      options: {
        enableSorting: true,
        enableGrouping: true,
      },
      cell: ({ row }: { row: any }) => {
        const isComposite = row.original.category === 'composite';
        const asSimpleIndicator = row.original.composite_as_simple_indicator;
        const label = category.items.find((item) => item.value === row.original.category)?.label || '';
        return (
          <CTag
            label={
              label + (isComposite && asSimpleIndicator ? ` (${t('indicator.selectValues.category.simple')})` : '')
            }
            bg={'primary.50'}
          />
        );
      },
    },
    {
      accessorKey: 'value',
      baseKey: 'value',
      header: t('collection.columnsHeader.value'),
      cell: ({ row }: { row: any }) => {
        const [value, setValue] = useState(row.original.value?.toString() || undefined);
        const canEdit =
          row.original.id && !(row.original.category === 'composite' && !row.original.composite_as_simple_indicator);
        const isQualitative = row.original.category === 'qualitative';
        const ref = useRef<HTMLDivElement>(null);

        const handleValueCommit = (_value: string) => {
          if (!row.original.value || row.original.value.toString() !== _value) {
            if (!areEqual(row.original.value, _value)) handleChange(row.original.id, _value, row.original.objective);
          }
        };

        const qualitativeCollection = createListCollection({
          items: [
            { value: '1', label: t('ui.yes') },
            { value: '0', label: t('ui.no') },
          ],
        });

        return (
          <Editable.Root
            ref={ref}
            activationMode="click"
            size={'sm'}
            disabled={!canEdit}
            value={value}
            onValueChange={(e) => {
              !isQualitative && setValue(e.value);
            }}
            onValueCommit={(e) => handleValueCommit(e.value)}
            borderColor={'red'}
          >
            {isLoading && <SkeletonText noOfLines={1} paddingBlock="16px" />}
            {!isLoading && (
              <>
                <Tooltip
                  showArrow
                  content={canEdit ? t('tooltip.clickEdit') : t('tooltip.notEditable')}
                  openDelay={100}
                  closeDelay={100}
                  contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
                >
                  <Editable.Preview
                    border={canEdit ? '1px solid #E4E4E7' : ''}
                    w={'100%'}
                    _hover={{
                      backgroundColor: canEdit ? { base: 'primary.100', _dark: 'primary.800' } : 'transparent',
                      cursor: canEdit ? 'pointer' : 'not-allowed',
                    }}
                  >
                    {isQualitative && row.original.id && value && (
                      <Text>{value === '1' ? t('ui.yes') : t('ui.no')}</Text>
                    )}
                    {isQualitative && !row.original.id && (
                      <Box>
                        <Text>
                          {Math.round(value * 100)} % {t('ui.yes')}
                        </Text>
                      </Box>
                    )}
                    {!isQualitative && value && (
                      <LocaleProvider locale="fr-FR">
                        <FormatNumber value={value} />
                      </LocaleProvider>
                    )}
                  </Editable.Preview>
                </Tooltip>
                {isQualitative ? (
                  <Select.Root
                    size="sm"
                    w="100%"
                    collection={qualitativeCollection}
                    value={value ? [value.toString()] : []}
                    onValueChange={(e) => {
                      const val = e.value.length > 0 ? e.value[0] : '';
                      setValue(val);
                      handleValueCommit(val);
                    }}
                    closeOnSelect
                  >
                    <Select.HiddenSelect />
                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder={`${t('ui.yes')} / ${t('ui.no')}`} />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.ClearTrigger />
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>
                    <Select.Positioner>
                      <Select.Content>
                        {qualitativeCollection.items.map((item) => (
                          <Select.Item key={item.value} item={item}>
                            {item.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                ) : (
                  <InputGroup endElement={<LuPencilLine color={'#1FA285'} />}>
                    <Editable.Input asChild>
                      <Input
                        type="text"
                        w="100%"
                        border="1px solid #1FA285"
                        borderRadius={'3px'}
                        focusVisibleRing="none"
                      />
                    </Editable.Input>
                  </InputGroup>
                )}
              </>
            )}
          </Editable.Root>
        );
      },
    },
    {
      accessorKey: 'indicator_unit',
      baseKey: 'indicator_year_setting___indicator___unit',
      header: t('collection.columnsHeader.unit'),
      options: {
        enableSorting: true,
        enableGrouping: true,
      },
    },
    {
      accessorKey: 'objective',
      baseKey: 'objective',
      header: t('collection.columnsHeader.objective'),
      cell: ({ row }: { row: any }) => {
        const [value, setValue] = useState(row.original.objective?.toString() || undefined);
        const canEdit = row.original.id;

        return (
          <Editable.Root
            size={'sm'}
            value={value}
            disabled={!canEdit}
            onValueChange={(e) => setValue(e.value)}
            onValueCommit={(e) => {
              if (!row.original.objective || row.original.objective.toString() !== e.value)
                if (!areEqual(row.original.objective, e.value))
                  handleChange(row.original.id, row.original.value?.toString(), e.value);
            }}
          >
            {isLoading && <SkeletonText noOfLines={1} paddingBlock="16px" />}
            {!isLoading && (
              <>
                <Tooltip
                  showArrow
                  content={canEdit ? t('tooltip.clickEdit') : t('tooltip.notEditable')}
                  openDelay={100}
                  closeDelay={100}
                  contentProps={{ css: { '--tooltip-bg': 'colors.primary' } }}
                >
                  <Editable.Preview
                    w={'100%'}
                    border={'1px solid #E4E4E7'}
                    _hover={{
                      backgroundColor: {
                        base: 'primary.100',
                        _dark: 'primary.800',
                      },
                      cursor: 'pointer',
                    }}
                  >
                    {value && (
                      <LocaleProvider locale="fr-FR">
                        <FormatNumber value={value} />
                      </LocaleProvider>
                    )}
                  </Editable.Preview>
                </Tooltip>
                <InputGroup startElement={<LuPencilLine color={'#1FA285'} />}>
                  <Editable.Input asChild>
                    <Input
                      type="text"
                      w="100%"
                      border="1px solid #1FA285"
                      borderRadius={'3px'}
                      focusVisibleRing="none"
                    />
                  </Editable.Input>
                </InputGroup>
              </>
            )}
          </Editable.Root>
        );
      },
    },
  ];

  const table = useReactTable({
    data: indicatorsPeriodsData?.data || [],
    // @ts-ignore
    columns,
    initialState: {
      grouping: [],
    },
    state: {
      grouping: grouping,
    },
    manualSorting: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const isPaginated =
    count !== 0 &&
    count > pageSize &&
    indicatorsPeriodsData?.data.length !== undefined &&
    indicatorsPeriodsData?.data.length < count;

  return (
    <>
      {isLoading ? (
        <Loader loadingText={t('loading.indicators.collecte')} />
      ) : (
        <>
          <Box maxH={'calc(90vh - 176px)'} borderColor={'transparent'}>
            <Table.ScrollArea
              maxH={'calc(90vh - 176px)'}
              whiteSpace={'break-spaces'}
              borderWidth={1}
              ref={scrollAreaRef}
              borderColor={'transparent'}
              w={'100%'}
            >
              <Table.Root size="md" interactive stickyHeader w={'100%'}>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Table.Header key={headerGroup.id} borderColor={'transparent'} w={'100%'}>
                    <Table.Row bg={'#F1F7FA'} w={'100%'}>
                      {headerGroup.headers.map((header) => (
                        <Table.ColumnHeader
                          key={header.id}
                          style={{ width: `${header.getSize()}px` }}
                          alignContent={'end'}
                          placeContent={'center'}
                          p={4}
                        >
                          <Box display={'flex'} flexDir={'column'}>
                            <Box color={{ base: 'gray.600', _dark: 'white' }} fontWeight={'semibold'} display={'flex'}>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </Box>
                          </Box>
                        </Table.ColumnHeader>
                      ))}
                    </Table.Row>
                  </Table.Header>
                ))}
                <Table.Body>
                  {table.getRowModel().rows.map((row) => (
                    <Table.Row key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <Table.Cell key={cell.id} p={4}>
                          {cell.getIsGrouped() ? (
                            <Box onClick={() => row.toggleExpanded()}>
                              <HStack>
                                <Icon
                                  size={'md'}
                                  rotate={row.getIsExpanded() ? '0deg' : '-90deg'}
                                  transition={'rotate 0.2s ease-in-out'}
                                >
                                  <MdOutlineExpandMore />
                                </Icon>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}{' '}
                                <CTag
                                  label={`${row.subRows.length}`}
                                  color={'primary.50'}
                                  bg={'primary.600'}
                                  ml={'auto'}
                                />
                              </HStack>
                            </Box>
                          ) : cell.getIsAggregated() ? (
                            flexRender(
                              cell.column.columnDef.aggregatedCell ?? cell.column.columnDef.cell,
                              cell.getContext()
                            )
                          ) : cell.getIsPlaceholder() ? null : (
                            flexRender(cell.column.columnDef.cell, cell.getContext())
                          )}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Table.ScrollArea>
          </Box>
          <Box>
            <Pagination isPaginated={isPaginated} count={count} page={page} pageSize={pageSize} navigate={navigate} />
          </Box>
          <IndicatorDrawer
            indicatorId={indicatorDetailsId ?? undefined}
            isOpen={indicatorDetailsId !== null}
            setIsOpen={(state: boolean) => setIndicatorDetailsId(state ? indicatorDetailsId : null)}
            editDisabled={true}
          />
          {count === 0 && filter !== '' && sort !== '' && <ResultNotFound />}
          {count === 0 && filter === '' && sort === '' && (
            <AbsoluteCenter>
              <EmptyState.Root>
                <EmptyState.Content>
                  <VStack textAlign="center">
                    <EmptyState.Title>{t('messages.hints.addCollecte.title')}</EmptyState.Title>
                    <EmptyState.Description>
                      {t('messages.hints.addFirstCollecte', {
                        manageLabel: t('pageTitle.indicators.manage'),
                      })}
                    </EmptyState.Description>
                  </VStack>
                </EmptyState.Content>
              </EmptyState.Root>
            </AbsoluteCenter>
          )}
        </>
      )}
    </>
  );
}

export default CollecteTable;
